import React from 'react';
import { Badge, Button, Checkbox, Text, Tooltip } from '@fluentui/react-components';
import { Edit16Regular, Dismiss16Regular } from '@fluentui/react-icons';
import { Transaction } from '../types';
import '../styles/register.css';

interface MobileTransactionCardProps {
  txn: Transaction;
  balance: number;
  isFuture: boolean;
  onToggleCleared: (id: number) => void;
  onEdit: (id: number) => void;
  onDelete: (id: number) => void;
}

function formatMoney(amount: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
}

function formatDate(dateStr: string): string {
  const d = dateStr.split('T')[0];
  const [year, month, day] = d.split('-');
  return `${month}/${day}/${year}`;
}

const ACTION_APPEARANCES: Record<string, 'success' | 'informative' | 'warning' | 'important'> = {
  deposit: 'success',
  draft: 'informative',
  check: 'warning',
  cash: 'important',
};

export const MobileTransactionCard: React.FC<MobileTransactionCardProps> = ({
  txn, balance, isFuture, onToggleCleared, onEdit, onDelete,
}) => {
  const badgeAppearance = ACTION_APPEARANCES[txn.action.toLowerCase()] || 'important';
  const isPayment = txn.payment !== null && txn.payment > 0;
  const amount = isPayment ? txn.payment! : txn.deposit!;

  const classes = [
    'mobile-card',
    txn.cleared ? 'cleared' : '',
    isFuture ? 'future-row' : '',
  ].filter(Boolean).join(' ');

  return (
    <div className={classes} onClick={() => onEdit(txn.id)}>
      <div className="mobile-card-top">
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span className="mobile-card-date">{formatDate(txn.date)}</span>
          <Badge appearance="filled" color={badgeAppearance} size="small">
            {txn.action}
          </Badge>
        </div>
        <div className="mobile-card-actions" onClick={e => e.stopPropagation()}>
          <Checkbox
            checked={txn.cleared}
            onChange={() => onToggleCleared(txn.id)}
            size="medium"
          />
          <Tooltip content="Edit" relationship="label">
            <Button appearance="subtle" size="small" icon={<Edit16Regular />}
              onClick={() => onEdit(txn.id)} />
          </Tooltip>
          <Tooltip content="Delete" relationship="label">
            <Button appearance="subtle" size="small" icon={<Dismiss16Regular />}
              onClick={() => onDelete(txn.id)} />
          </Tooltip>
        </div>
      </div>

      <div className="mobile-card-payee">{txn.payee}</div>

      <div className="mobile-card-amount">
        <span className={`mobile-card-amount-value ${isPayment ? 'payment' : 'deposit'}`}>
          {isPayment ? '-' : '+'}{formatMoney(amount)}
        </span>
        <span className={`mobile-card-balance ${balance < 0 ? 'negative' : ''}`}>
          Bal: {formatMoney(balance)}
        </span>
      </div>

      {txn.notes && <div className="mobile-card-notes">{txn.notes}</div>}
    </div>
  );
};
